'use client';

import { c as _c } from "react/compiler-runtime";
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { TierBadge } from '@/domains/pages/model-savings/components/tier-badge';
import { MODEL_ORDER, MODEL_SAVINGS_STAT_NUMBER_CLASS, MODEL_TIERS, MODEL_TIER_COLORS, MODEL_TIER_LABELS } from '@/domains/pages/model-savings/constants';
import type { ModelMixSegment } from '@/domains/pages/model-savings/types';
import { mixPercent } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
type RecommendedMixLabelProps = {
  mix: ModelMixSegment[];
  /**
   * `inline` trails a row as an aside, in tier colours. `cards` is the larger treatment for
   * where the mix is the subject, carrying its tier colour on the border alone.
   */
  variant?: 'inline' | 'cards';
  className?: string;
};

/** The optimal mix broken out by tier, cheapest tier first. */
export function RecommendedMixLabel(t0) {
  const $ = _c(17);
  const {
    mix,
    variant: t1,
    className
  } = t0;
  const variant = t1 === undefined ? "inline" : t1;
  let t2;
  let t3;
  if ($[0] !== className || $[1] !== mix || $[2] !== variant) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t4;
      if ($[5] !== mix) {
        t4 = modelId => ({
          modelId,
          percent: mixPercent(mix, modelId)
        });
        $[5] = mix;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      const segments = MODEL_ORDER.map(t4).filter(_temp);
      if (segments.length === 0) {
        t3 = null;
        break bb0;
      }
      if (variant === "cards") {
        let t5;
        if ($[7] !== className) {
          t5 = cn("grid grid-cols-4 gap-3", className);
          $[7] = className;
          $[8] = t5;
        } else {
          t5 = $[8];
        }
        let t6;
        if ($[9] !== mix) {
          t6 = MODEL_ORDER.map(modelId_0 => {
            const tier = MODEL_TIERS[modelId_0];
            return <div key={modelId_0} className="rounded-lg border px-4 py-3" style={{
              borderColor: MODEL_TIER_COLORS[tier]
            }}><p className="text-[10px] text-muted-foreground">{MODEL_TIER_LABELS[tier]}</p><p className={cn("mt-1 text-foreground", MODEL_SAVINGS_STAT_NUMBER_CLASS, AIFI_DATA_NUMBER_CLASS)}>{formatScore(mixPercent(mix, modelId_0), {
                  decimals: 0
                })}%</p></div>;
          });
          $[9] = mix;
          $[10] = t6;
        } else {
          t6 = $[10];
        }
        let t7;
        if ($[11] !== t5 || $[12] !== t6) {
          t7 = <div className={t5}>{t6}</div>;
          $[11] = t5;
          $[12] = t6;
          $[13] = t7;
        } else {
          t7 = $[13];
        }
        t3 = t7;
        break bb0;
      }
      let t5;
      if ($[14] !== className) {
        t5 = cn("flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground", className);
        $[14] = className;
        $[15] = t5;
      } else {
        t5 = $[15];
      }
      let t6;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <span className="shrink-0">Optimal mix:</span>;
        $[16] = t6;
      } else {
        t6 = $[16];
      }
      t2 = <div className={t5}>{t6}{segments.map(_temp2)}</div>;
    }
    $[0] = className;
    $[1] = mix;
    $[2] = variant;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  return t2;
}
function _temp2(segment_0) {
  return <TierBadge key={segment_0.modelId} tier={MODEL_TIERS[segment_0.modelId]} value={`${formatScore(segment_0.percent, {
    decimals: 0
  })}%`} indicator="dot" />;
}
function _temp(segment) {
  return segment.percent > 0;
}